import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Info } from '@mui/icons-material'
import { Box, Grid, TableContainer } from '@mui/material'
import { Truncate } from '@re-dev/react-truncate'

import { GRTable, GRTableColumn, SortOrder } from '../../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { pricePointCurrencySymbol } from '../../../../internal/features/live-events-tracking/components/PricePointContainer/PricePointSelectMenu/PricePointSelectMenu'
import { PricePoint } from '../../../../internal/features/live-events-tracking/types/PricePoint'
import { calculateCurrencyMedians, getAllCurrencyValues } from '../../../../internal/helpers/currencyCalculations'
import { Currency } from '../../../../internal/types/Currency'
import { QuestionTooltip } from '../../../game/components/GameKeyIndicator/GameKeyIndicator'
import { getPricePointUnitValue } from '../../utils/utils'
import './LiveEventPricePoints.scss'

interface LiveEventPricePointsProps {
  pricePoints: PricePoint[]
  currencies: Currency[] | undefined
}

const TextWithLineBreaks: React.FC<{ text: string }> = ({ text }) => {
  const parts = text.split('\n')
  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {part}
          {index < parts.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  )
}

const TruncatedText: React.FC<{ text: string }> = ({ text }) => {
  const [isTruncated, setIsTruncated] = useState(false)

  return (
    <Grid container direction="row" wrap="nowrap" sx={{ justifyContent: 'center', alignItems: 'center' }}>
      <Grid item xs>
        <Truncate
          lines={3}
          onTruncate={(didTruncate) => {
            setIsTruncated(didTruncate)
          }}
        >
          <TextWithLineBreaks text={text} />
        </Truncate>
      </Grid>
      {isTruncated && (
        <Grid item marginLeft={1}>
          <GRTooltip content={<TextWithLineBreaks text={text} />}>
            <Info color="primary" />
          </GRTooltip>
        </Grid>
      )}
    </Grid>
  )
}

const LiveEventPricePoints: React.FC<LiveEventPricePointsProps> = ({ pricePoints, currencies }) => {
  const { t } = useTranslation()
  const customProps = useMemo(() => ({ currencies }), [currencies])

  const pricePointColumns: GRTableColumn<PricePoint, typeof customProps>[] = useMemo(
    () => [
      {
        headerCellProps: { sx: { fontWeight: 700, textAlign: 'center', maxWidth: 20 } },
        labelAccessor: () => t('live-events:price_point'),
        accessor: ({ customTableProps, row }) => {
          if (row.currencyId) {
            const unitPrice = getPricePointUnitValue(row, customTableProps?.currencies || [])
            if (unitPrice) {
              const matchedCurrency = customTableProps?.currencies?.find((currency) => currency.id === row.currencyId)

              // Calculate median values for tooltip explanation
              let tooltipExplanation = matchedCurrency?.description || row.description
              if (matchedCurrency) {
                const allValues = getAllCurrencyValues(matchedCurrency)
                const { medianUsd, medianGame, validValuesCount } = calculateCurrencyMedians(allValues)

                if (validValuesCount > 0) {
                  tooltipExplanation = `${t('common:median_label')}: $${medianUsd.toFixed(2)} - ${Math.round(medianGame)} ${matchedCurrency.name}`
                }
              }

              return (
                <Grid container spacing={0.5} direction="column" sx={{ justifyContent: 'center', alignItems: 'center' }}>
                  <Grid item>
                    <QuestionTooltip explanation={tooltipExplanation} fontSize="12px">
                      {row.value + ' ' + unitPrice.currencyName}
                    </QuestionTooltip>
                  </Grid>
                  <Grid item>
                    {'≈ '}
                    <strong>
                      {pricePointCurrencySymbol}
                      {unitPrice.value}
                    </strong>
                  </Grid>
                </Grid>
              )
            }
          }

          return (
            <strong>
              {pricePointCurrencySymbol.toUpperCase()}
              {Math.round(row.value * 100) / 100}
            </strong>
          )
        },
        cellProps: { sx: { textAlign: 'center' } },
        sortable: true,
        sortAccessor: ({ customTableProps, row }) => {
          if (row.currencyId) {
            const unitPrice = getPricePointUnitValue(row, customTableProps?.currencies || [])

            if (unitPrice) {
              return unitPrice.value
            }
          }
          return row.value
        },
        sortOrder: SortOrder.ASC,
      },
      {
        headerCellProps: { sx: { fontWeight: 700, textAlign: 'center' } },
        labelAccessor: () => t('common:description'),
        accessor: ({ row }) => {
          return <TruncatedText text={row.description} />
        },
        cellProps: { align: 'left' },
        sortable: false,
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState<GRTableColumn<PricePoint, typeof customProps>[]>(pricePointColumns)

  const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<PricePoint, typeof customProps>[]) => {
    setColumns(updatedColumns)
  }, [])

  return (
    <TableContainer component={Box} className="LiveEventPricePoints">
      <GRTable
        columns={columns}
        rows={pricePoints}
        rowIdKey={(row, index) => `${row.value}-${index}`}
        customProps={customProps}
        onColumnsUpdated={handleColumnsUpdate}
        striped
        gridlines
      />
    </TableContainer>
  )
}

export default LiveEventPricePoints
